import * as React from 'react'

import { RenderButton, ButtonWrapper } from '../../atoms/Button/RenderButton'
import Headings from '../../atoms/Heading'
import Image from '../../atoms/Image'
import Paragraph from '../../atoms/Paragraph'
import Video from '../../atoms/Video'

interface Props {
  content?: any
  background?: string
  textAlignment?: boolean
  cardBackground?: string
  marginBottom?: boolean
}

/**
 * Callout Component
 * @param content - Content from the CMS.
 * @param cardBackground (optional) - Background of the component. Expected values: "white" | "teal" | "lightPurple" | "orange", Default: "transparent"
 * @param background (optional) - Background color of the component. Expected values: "white" | "purple" | "lightPurple" | "orange" | "teal", Default: "white"
 * @param textAlignment (optional) - Text Alignment of the component. Expected values: true | false, Default: "center"
 * @param marginBottom (optional) - Bottom margin of the component. Expected values: true | false, Default: true
 */
const Callout = ({
  content,
  background = 'white',
  cardBackground = 'transparent',
  textAlignment,
  marginBottom = true,
}: Props) => {
  background = background.toLowerCase()
  cardBackground = cardBackground.toLowerCase()
  const bgColor =
    background === 'lightpurple'
      ? 'bg-purple-10'
      : background === 'orange'
        ? 'bg-orange-10'
        : background === 'teal'
          ? 'bg-teal-10'
          : background === 'purple'
            ? 'bg-purple-100'
            : 'bg-white'
  const bgCardColor =
    cardBackground === 'lightpurple'
      ? 'bg-purple-10'
      : cardBackground === 'orange'
        ? 'bg-orange-10'
        : cardBackground === 'teal'
          ? 'bg-teal-10'
          : background === 'purple'
            ? 'bg-purple-100'
            : 'bg-white'
  const columns = content.cardLayout === '3' ? 'xl:w-[32%]' : content.cardLayout === '4' ? 'xl:w-[23.6%]' : 'xl:w-[49%]'
  const layout = `md:w-[48.25%] lg:w-[48.7%] ${columns} md:max-w-[50%]`
  const textAlign = textAlignment === true ? 'left' : textAlignment === false ? 'right' : 'center'

  const headingColor =
    background === 'lightpurple' ? 'text-purple-100' : background === 'purple' ? 'text-white' : 'text-black'

  const textColor = background === 'purple' ? 'text-white' : 'text-black'

  const isIconIncluded = (str) => {
    return str && str.toLowerCase().includes('icon')
  }

  return (
    <>
      <div data-testid="callout" className={`Callout layoutSpacing ${bgColor}`}>
        <div
          className={`w-full ${bgColor !== 'bg-white' ? 'sectionSpacing' : 'sectionBottomSpacing'} ${
            bgColor !== 'bg-white' && marginBottom ? 'sectionBottomMargin' : ''
          } layout } layout`}
        >
          {(content?.headline || content?.description) && (
            <div className="w-full">
              {content?.headline && (
                <Headings
                  content={content?.headline}
                  variant="H2"
                  classes={`${headingColor} ${content?.description ? '' : '!pb-24'}`}
                />
              )}
              {content?.subheadline && (
                <Headings
                  content={content?.subheadline}
                  variant="H3"
                  classes={`${headingColor} ${content?.description ? '' : '!pb-24'}`}
                />
              )}
              {content?.headline !== 'Phone' && content?.description && (
                <Paragraph content={content?.description} classes={`pb-16 ${textColor}`} />
              )}
            </div>
          )}
          <div className="flex flex-wrap justify-start gap-16">
            {content?.features?.map((item) => (
              <>
                {item?.sys?.contentType?.sys?.id === 'blockInfoFeatureComponent' && (
                  <div
                    key={item?.fields?.headline}
                    className={`flex flex-col ${
                      bgCardColor === 'bg-white' ? 'bg-transparent' : bgCardColor
                    } w-full ${content?.features.length > 1 ? layout : ''}`}
                  >
                    {item?.fields?.contentImage?.fields?.file?.url &&
                      !isIconIncluded(item?.fields?.contentImage?.fields?.title) &&
                      !isIconIncluded(item?.fields?.contentImage?.fields?.description) && (
                        <div
                          className={`${textAlign === 'right' ? 'ml-auto' : textAlign === 'left' ? 'mr-auto' : 'm-auto'}`}
                        >
                          <Image
                            imageUrl={item?.fields?.contentImage?.fields?.file?.url}
                            altText={item?.fields?.contentImage?.fields?.title}
                          />
                        </div>
                      )}
                    {item?.fields?.contentImage?.fields?.file?.url &&
                      (isIconIncluded(item?.fields?.contentImage?.fields?.title) ||
                        isIconIncluded(item?.fields?.contentImage?.fields?.description)) && (
                        <div
                          className={`pb-16 md:pb-16 ${textAlign === 'right' ? 'ml-auto' : textAlign === 'left' ? 'mr-auto' : 'm-auto'}`}
                        >
                          <Image
                            imageUrl={item?.fields?.contentImage?.fields?.file?.url}
                            altText={item?.fields?.contentImage?.fields?.title}
                            width="56"
                          />
                        </div>
                      )}
                    {item?.fields?.headline && item?.fields?.headline !== null && (
                      <div
                        className={`${
                          !item?.description && item?.description === null && 'flex-grow'
                        } pr-16 text-${textAlign}`}
                      >
                        <Headings variant="H4" content={item?.fields?.headline} classes={`!text-80 ${headingColor}`} />
                      </div>
                    )}
                    {item?.fields?.subHeadline && item?.fields?.subHeadline !== null && (
                      <div
                        className={`${
                          !item?.description && item?.description === null ? 'flex-grow' : ''
                        } pr-16 text-${textAlign}`}
                      >
                        {/* <Headings
                            variant={"H4"} // Condition Added to Match the Figma Design, It could be modified based on Business demand.
                            content={item?.fields?.subHeadline}
                            classes={`${textColor}`}
                          /> */}
                        <Paragraph content={item?.fields?.subHeadline} classes={`${textColor} text-xl pb-16`} />
                      </div>
                    )}
                    {item?.fields?.contentDescription && item?.fields?.contentDescription !== null && (
                      <>
                        <div className={`flex-grow pr-16 text-${textAlign}`}>
                          <Paragraph content={item?.fields?.contentDescription} classes={`${textColor}`} />
                        </div>
                      </>
                    )}
                    {item?.fields?.video && (
                      <div className={`pb-12 pr-16 md:pb-12 text-${textAlign}`}>
                        <Video
                          videoUrl={item?.fields?.video}
                          classes={`${item?.fields?.caption ? 'mb-16' : ''}`}
                          caption={item?.fields?.caption}
                        />
                      </div>
                    )}
                    {item?.fields?.contentCtaLinkToPage && item?.fields?.contentCtaLinkToPage !== null && (
                      <div
                        className={`pb-16 pr-16 ${textAlign === 'right' ? 'ml-auto' : textAlign === 'left' ? 'mr-auto' : 'm-auto'} pt-24`}
                      >
                        <RenderButton item={item?.fields?.contentCtaLinkToPage[0]} />
                      </div>
                    )}
                  </div>
                )}
              </>
            ))}
            {content?.assets?.map((item) => (
              <>
                {item?.fields?.file?.contentType.includes('image') && (
                  <Image
                    imageUrl={item?.fields?.file?.url}
                    altText={item?.fields?.description}
                    className={`mb-16 ${isIconIncluded(item?.fields?.title) && isIconIncluded(item?.fields?.description) ? 'w-56' : ''}`}
                  />
                )}
                {item?.fields?.file?.contentType.includes('video') && <Video videoUrl={item?.fields?.file?.url} />}
              </>
            ))}
          </div>

          <div className={`flex justify-center ${content.spacingAboveButtons ? 'mt-24' : ''}`}>
            <ButtonWrapper content={content} columns={columns} />
          </div>

          {content?.headline === 'Phone' && content?.description && (
            <Paragraph content={content?.description} classes="" />
          )}
        </div>
      </div>
    </>
  )
}

export default Callout
